import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { BASE_URL } from "../../../../services/configUrls";
import { AppleStyleSpinner } from "./AppleStyleSpinner";

const AddFeatureModal = ({
  open,
  onClose,
  features,
  selectedFeatures,
  onFeatureToggle,
  featureTypes,
  featureStatusValues,
  onFeatureTypeChange,
  onFeatureStatusValueChange,
  onOpenCreateTextStatus,
  onOpenAddNewFeature,
  featureSearchQuery,
  onSearchQueryChange,
  setCurrentEditingFeature,
  membershipId,
  onRefreshMembership,
}) => {
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchFeatures();
      fetchStatusOptions();
    }
  }, [open]);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-feature`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (result && Array.isArray(result)) {
        setAvailableFeatures(result);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (result && Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
          color: getColorForSymbol(status.symbol),
        }));
        setStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const getColorForSymbol = (symbol) => {
    switch (symbol) {
      case "✓":
        return { bg: "#D1FAE5", color: "#065F46" };
      case "✗":
        return { bg: "#F3F4F6", color: "#374151" };
      case "*":
        return { bg: "#DBEAFE", color: "#1E40AF" };
      default:
        return { bg: "#E0E7FF", color: "#3730A3" };
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onSearchQueryChange("");
      onClose();
    }
  };

  const handleAddFeatureBulk = async () => {
    if (selectedFeatures.length === 0 || !membershipId) {
      return;
    }

    setSubmitting(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const featureStatusMappings = selectedFeatures.map((featureName) => {
        const feature = availableFeatures.find(
          (f) => f.feature_name === featureName
        );

        if (feature.type === "Count") {
          return {
            feature_id: feature.feature_id,
            count_value: parseInt(featureStatusValues[featureName] || "0"),
          };
        } else {
          const statusValue = featureStatusValues[featureName] || "TBA";
          const statusOption = statusOptions.find(
            (opt) => opt.text === statusValue
          );

          return {
            feature_id: feature.feature_id,
            status_option_id: statusOption?.id || null,
          };
        }
      });

      const payload = {
        membership_id: membershipId,
        feature_status_mappings: featureStatusMappings,
      };

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature-status-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (onRefreshMembership) {
          await onRefreshMembership(membershipId);
        }

        onSearchQueryChange("");
        onClose();
      } else {
        alert(result.message || "Failed to add features");
      }
    } catch (error) {
      console.error("Error adding features:", error);
      alert("Error adding features. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Fixed function to preserve API order
  const getFilteredAndSortedFeatures = () => {
    const filtered = availableFeatures.filter(
      (feature) =>
        !features.some((f) => f.name === feature.feature_name) &&
        feature.feature_name
          .toLowerCase()
          .includes(featureSearchQuery.toLowerCase())
    );

    // Separate selected and unselected features while maintaining API order
    const selected = [];
    const unselected = [];

    filtered.forEach((feature) => {
      if (selectedFeatures.includes(feature.feature_name)) {
        selected.push(feature);
      } else {
        unselected.push(feature);
      }
    });

    // Return selected first, then unselected (both in original API order)
    return [...selected, ...unselected];
  };

  const sortedFeatures = getFilteredAndSortedFeatures();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
      TransitionProps={{
        timeout: 400,
      }}
      PaperProps={{
        sx: {
          animation: open ? "slideUp 0.3s ease-out" : "none",
          "@keyframes slideUp": {
            from: {
              opacity: 0,
              transform: "translateY(20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "15px",
          fontWeight: 600,
          pb: 2,
        }}
      >
        Select Features
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search features..."
          value={featureSearchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          disabled={submitting}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF", mr: 1 }} />
            ),
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              fontSize: "13px",
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
            px: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: "#6C757D",
              fontWeight: 500,
            }}
          >
            {selectedFeatures.length > 0
              ? `${selectedFeatures.length} selected`
              : "Select features to add"}
          </Typography>
        </Box>

        <Box
          sx={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #E5E7EB",
            borderRadius: "6px",
            backgroundColor: "#FAFBFC",
            opacity: submitting ? 0.6 : 1,
            pointerEvents: submitting ? "none" : "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F3F4F6",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "#9CA3AF",
              },
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: "6px",
                  border: "2px dashed #2D7FF9",
                  backgroundColor: "#F0F7FF",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#E6F2FF",
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={onOpenAddNewFeature}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#2D7FF9",
                      fontWeight: 600,
                    }}
                  >
                    Add New Feature
                  </Typography>
                </Box>
              </Box>

              {sortedFeatures.map((feature) => (
                <Box
                  key={feature.feature_id}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: "6px",
                    border: selectedFeatures.includes(feature.feature_name)
                      ? "1.5px solid #2D7FF9"
                      : "1px solid #E5E7EB",
                    backgroundColor: selectedFeatures.includes(
                      feature.feature_name
                    )
                      ? "#F0F7FF"
                      : "#FFFFFF",
                    "&:hover": {
                      backgroundColor: selectedFeatures.includes(
                        feature.feature_name
                      )
                        ? "#E6F2FF"
                        : "#F9FAFB",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFeatures.includes(
                          feature.feature_name
                        )}
                        onChange={() => onFeatureToggle(feature.feature_name)}
                        size="small"
                        sx={{
                          color: "#9CA3AF",
                          p: 0.5,
                          "&.Mui-checked": {
                            color: "#2D7FF9",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#212529",
                          fontWeight: 500,
                        }}
                      >
                        {feature.feature_name}
                      </Typography>
                    }
                    sx={{
                      mx: 0,
                      mb: selectedFeatures.includes(feature.feature_name)
                        ? 1
                        : 0,
                    }}
                  />

                  {selectedFeatures.includes(feature.feature_name) && (
                    <Box sx={{ ml: 4, mt: 1 }}>
                      {feature.type === "Count" ? (
                        <>
                          {!featureTypes[feature.feature_name] &&
                            onFeatureTypeChange(feature.feature_name, "count")}
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Count Value"
                            value={
                              featureStatusValues[feature.feature_name] || "0"
                            }
                            onChange={(e) =>
                              onFeatureStatusValueChange(
                                feature.feature_name,
                                e.target.value
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        </>
                      ) : (
                        <>
                          {!featureTypes[feature.feature_name] &&
                            onFeatureTypeChange(feature.feature_name, "text")}
                          <Autocomplete
                            freeSolo
                            fullWidth
                            options={statusOptions.map((st) => st.text)}
                            value={
                              featureStatusValues[feature.feature_name] || "TBA"
                            }
                            onChange={(event, newValue) => {
                              if (newValue) {
                                onFeatureStatusValueChange(
                                  feature.feature_name,
                                  newValue
                                );
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              onFeatureStatusValueChange(
                                feature.feature_name,
                                newInputValue
                              );
                            }}
                            ListboxProps={{
                              style: { maxHeight: "250px", padding: 0 },
                            }}
                            ListboxComponent={React.forwardRef((props, ref) => {
                              const { children, ...other } = props;
                              return (
                                <ul
                                  {...other}
                                  ref={ref}
                                  style={{ padding: 0, margin: 0 }}
                                >
                                  {/* Sticky "Create New" option */}
                                  <Box
                                    component="li"
                                    onClick={() => {
                                      setCurrentEditingFeature(
                                        feature.feature_name
                                      );
                                      onOpenCreateTextStatus();
                                    }}
                                    sx={{
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 10,
                                      backgroundColor: "#FFFFFF",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      padding: "8px 14px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #E5E7EB",
                                      listStyle: "none",
                                      "&:hover": {
                                        backgroundColor: "#F0F9FF",
                                      },
                                    }}
                                  >
                                    <AddIcon
                                      sx={{ fontSize: 16, color: "#2D7FF9" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        color: "#2D7FF9",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Create New...
                                    </Typography>
                                  </Box>
                                  {/* Scrollable status list */}
                                  {children}
                                </ul>
                              );
                            })}
                            renderOption={(props, option) => (
                              <li {...props} key={option}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: "3px",
                                      backgroundColor:
                                        statusOptions.find(
                                          (st) => st.text === option
                                        )?.color.bg || "#E0E7FF",
                                      color:
                                        statusOptions.find(
                                          (st) => st.text === option
                                        )?.color.color || "#3730A3",
                                      fontSize: "11px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {statusOptions.find(
                                      (st) => st.text === option
                                    )?.symbol || ""}
                                  </Box>
                                  <Typography sx={{ fontSize: "13px" }}>
                                    {option}
                                  </Typography>
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Status"
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    fontSize: "13px",
                                  },
                                }}
                              />
                            )}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {!loading && sortedFeatures.length === 0 && !featureSearchQuery && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: "#9CA3AF",
                textAlign: "center",
                py: 4,
              }}
            >
              All features have been added
            </Typography>
          )}
          {!loading && sortedFeatures.length === 0 && featureSearchQuery && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: "#9CA3AF",
                textAlign: "center",
                py: 4,
              }}
            >
              No features found matching "{featureSearchQuery}"
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          size="small"
          disabled={submitting}
          sx={{ fontSize: "13px" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddFeatureBulk}
          variant="contained"
          disabled={selectedFeatures.length === 0 || submitting}
          startIcon={
            submitting ? (
              <AppleStyleSpinner />
            ) : (
              <AddIcon sx={{ fontSize: 16 }} />
            )
          }
          size="small"
          sx={{ fontSize: "13px" }}
        >
          {submitting
            ? "Adding..."
            : `Add ${
                selectedFeatures.length > 0
                  ? `(${selectedFeatures.length})`
                  : ""
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFeatureModal;
